import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { hasValue, isNil } from 'swipeclock-frontend';
import { v4 as uuidv4 } from 'uuid';
import {
  wizardSteps as defaultWizardSteps,
  wizardStepModel,
} from 'models/accrualsSteps';
import { wizardStepTransformer } from 'helpers/wizardStepTransformer';
import { route } from 'models/routes';
import {
  getAccrualsWizardLink,
  getAccrualsWizardEditLink,
} from 'models/routes';
import { twpApiURL } from '../api';
import { getError } from 'helpers/errorHelper';
import { sleep } from 'swipeclock-frontend';
import { sessionStorageKeys } from 'constants/storageConstants';
import {
  sessionStorage as sessionStorageHelper,
  deepEqual,
} from 'swipeclock-frontend';
// HELPERS

// Helper for sleeping just enough to show some loading time
const transitionSleep = async () => await sleep(350);

// Helper for parsing bool values out of local storage
const parseBool = (value, defaultValue = false) =>
  (['true', 'false', true, false].includes(value) && JSON.parse(value)) ||
  defaultValue;

export const fetchPunchCategories = createAsyncThunk(
  'accruals/fetchPunchCategories',
  async (_, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const api = twpApiURL(user);

    try {
      const response = await api.fetchPunchCategories();
      const punchCategoryList = response?.data?.PunchCategories.filter(
        (e) => e.IsTimeOffRequestable
      ).map((category) => category.Name && category.Name);
      return punchCategoryList || [];
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

export const fetchPolicies = createAsyncThunk(
  'accruals/fetchPolicies',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const user = await getState().user;
    const api = twpApiURL(user);

    try {
      const { data } = (await api.fetchAccrualPolicies()) ?? {};
      const { Policies, ManagedInTwp } = data ?? {
        ManagedInTwp: false,
        Policies: [],
      };

      // SideEffect...let's set permission now to see if Accruals is managed in twp or not.
      // Does this get set or called everytime we fetch policies?
      // Yes and No. It will make a dispatch but state will most certainely stay the same.
      // But what about uneccessary checks or renders?
      // Well it's very important how we use this bool. But remember if each time we call and it's
      // value is the same state won't do anything.
      // However, we also have our bases covered.
      // Spoiler alert !!! We're using a hook to redirect based on this value
      // that will only run once when a component mounts.
      // Please see hooks/userRedirect::useRedirectIfManagedInTwp.
      // The secret sauce:
      // We use a ref to keep track and make sure it's only every called once per component mount.
      // In otherwords, a user would need to close the tab in their browser
      // and reload another tab with Accruals to get this to call again.
      dispatch(setManagedInTwp(ManagedInTwp));
      return Policies?.reverse() ?? []; // we reverse to show newest first in ASC order
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

export const deletePolicy = createAsyncThunk(
  'accruals/deletePolicy',
  async ({ Id }, { getState, dispatch, rejectWithValue, fulfillWithValue }) => {
    const user = await getState().user;
    const { policies } = await getState().accruals;
    const filteredPolicies = policies.filter((p) => p?.ID !== Id);
    const accrualPolicy = policies.find((policy) => policy.ID === Id);
    const { AccrualPolicyName } = accrualPolicy;
    const api = twpApiURL(user);

    try {
      await api.postAccrualPolicies(filteredPolicies);
      await transitionSleep();
      dispatch(fetchPolicies()); // Need to get updated versions for they are not returned from API when calling delete
      return fulfillWithValue({
        message: `Success! The ${AccrualPolicyName} was deleted.`,
      });
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

export const submitPolicy = createAsyncThunk(
  'accruals/postPolicies',
  async (
    { policy, history },
    { getState, dispatch, rejectWithValue, fulfillWithValue }
  ) => {
    const user = await getState().user;
    const { editing: isEditing, policies } = await getState().accruals;
    const filteredPolicies = policies.filter((p) => p?.ID !== policy?.ID);
    const api = twpApiURL(user);

    try {
      if (!policy.ID) {
        throw new Error('No id was found for the policy');
      }
      await api.postAccrualPolicies([...filteredPolicies, policy]);
      await transitionSleep();

      const snackbarMessage = isEditing
        ? `${policy?.AccrualPolicyName} was successfully updated.`
        : `Success! ${policy?.AccrualPolicyName} was created.`;

      if (history) {
        await history.push(`/${route.policies}`);
      }
      dispatch(setNewPolicyCreated(true));
      dispatch(fetchPolicies()); // Need to get updated version for they are not returned from API when calling submit
      return fulfillWithValue({ message: snackbarMessage });
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

// This is the backbone of saving state.
// 1. Look for an id in the url params
// 2. See if we're interested in continuation by confirming pathname
// 3. if params id value is nil, then redirect. Nothing to do for no id
// 4. If the param id is NOT nil but the policy ID is nil,
//    then we need to assign the params id to the policy ID.
//    If we have interest ( meaning desired pathname included ), and
//    the policy ID is nil ( not param id ) then do the following:
//    a. Find the policy that matches the param id
//    b. If we find a policy with said id inside policies then
//       update the policy with the retrieved policy inside policies/.
//    c. If we do NOT find a policy inside policies then it's a new policy
//       or the page was refreshed ansd we lost the policy.
//       Therefore, try and update policy with what is found in the cookie cache or
//       if nothing is found in the cookie cache, create a new policy with said id.
//       Last: If no stored policy was found in policies and we updated the policy via
//       a cookie then clear out the cookie.
// 5. If inside wizard then update the wizard step
//    Note: This is a side-effect that probably needs to be moved out.
//    However, haven't found a good place yet to run this code independently
export const updatePolicyCache = createAsyncThunk(
  'accruals/updatePolicyCache',
  async (args, { getState, dispatch, rejectWithValue }) => {
    const { location, params } = args;
    const { policy, policies } = await getState().accruals;
    const { pathname } = location;
    // ID in path is used to either get a policy from policies
    // or keep track a a policy while creating
    const id = params?.accrualsPolicyId;

    // We want to guard against the url path
    // only intrested if in the wizard
    // Note: in future may add other paths to include
    const hasInterest =
      pathname.includes(route.accrualsWizard) ||
      pathname.includes(route.policy);

    if (isNil(id) || id === 'undefined' || id === 'null') {
      return rejectWithValue({ error: getError() });
    }

    if (
      hasInterest &&
      (isNil(policy?.ID) || (!isNil(policy?.ID) && policy?.ID !== id))
    ) {
      // We're in a place of interest but do not have a policy ID
      // Or, We're in a place of interest and have a policy ID, but it is
      // different than the id in the url.
      // Let's see check store for a policy:

      // If we have an id let's see if it's in policies
      const storedPolicy = policies?.find((e) => e?.ID === id);

      if (hasValue(storedPolicy)) {
        // Make sure we update editing, if creating a policy we don't need this
        // therefore only doing this here after checking we have a "stored" policy.
        dispatch(updatePolicy(storedPolicy));
      }

      if (!hasValue(storedPolicy)) {
        const item = sessionStorageHelper.getItem(
          sessionStorageKeys.ACCRUAL_POLICY
        );

        dispatch(
          updatePolicy(hasValue(item) ? { ID: id, ...item } : { ID: id })
        );
      }
    }
    return id;
  }
);

// Let's keep things simple. We will use policy
// as our cache and place of store
//
// policy's params/vars are uppercase
// to stay in line with API
const slice = createSlice({
  name: 'accruals',
  initialState: {
    storage: {
      doNotShowDialogForEdit: parseBool(
        sessionStorageHelper.getItem(
          sessionStorageKeys.ACCRUAL_DO_NOT_SHOW_FOR_EDIT
        )
      ),
      doNotShowDialogForGoBackToPolicies: parseBool(
        sessionStorageHelper.getItem(
          sessionStorageKeys.ACCRUAL_DO_NOT_SHOW_FOR_GO_BACK
        )
      ),
    },

    editing: false,
    isFetchingPolicies: false,
    isRemovingPolicy: false,
    isSavingPolicy: false,
    ManagedInTwp: false,
    newPolicyCreated: false,
    punchCategories: undefined,
    policy: {
      ID: undefined,
    },
    policies: undefined,
    wizard: {
      completed: false,
      feature: wizardStepModel.create,
      hasSummary: false,
      isRunning: false,
      summaryHidden: false,
      step: 0,
      steps: defaultWizardSteps,
    },
  },
  reducers: {
    cancelWizard(state) {
      state.wizard = {
        completed: false,
        feature: wizardStepModel.create,
        hasSummary: false,
        isRunning: false,
        summaryHidden: false,
        step: 0,
        steps: defaultWizardSteps,
      };
    },
    clearPolicy(state) {
      state.policy = {
        ID: undefined,
      };
    },
    setDoNotShowDialogForGoBackToPolicies(state, action) {
      state.storage.doNotShowDialogForGoBackToPolicies = action.payload.value;
    },
    setDoNotShowDialogForEdit(state, action) {
      state.storage.doNotShowDialogForEdit = action.payload.value;
    },
    setEditing(state, action) {
      state.editing = action.payload?.editing;
    },
    setManagedInTwp(state, action) {
      state.ManagedInTwp = action.payload;
    },
    setNewPolicyCreated(state, action) {
      state.newPolicyCreated = action.payload;
    },
    setPolicies(state, action) {
      state.policies = action.payload.policies;
    },
    setWizardStep(state, action) {
      state.wizard.feature = action.payload?.feature;
      state.wizard.hasSummary = action.payload?.step >= 1;
      state.wizard.step = action.payload?.step;
    },
    setWizardRunning(state, action) {
      state.wizard.isRunning = action.payload;
    },
    updatePolicy(state, action) {
      state.policy = { ...state.policy, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deletePolicy.pending, (state) => {
        state.isRemovingPolicy = true;
      })
      .addCase(deletePolicy.fulfilled, (state) => {
        state.isRemovingPolicy = false;
      })
      .addCase(deletePolicy.rejected, (state) => {
        state.isRemovingPolicy = false;
      })
      .addCase(fetchPolicies.pending, (state) => {
        state.isFetchingPolicies = true;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.isFetchingPolicies = false;
        state.policies = action.payload;
      })
      .addCase(fetchPolicies.rejected, (state) => {
        state.isFetchingPolicies = false;
        state.newPolicyCreated = false;
      })
      .addCase(fetchPunchCategories.fulfilled, (state, action) => {
        state.punchCategories = action.payload;
      })
      .addCase(submitPolicy.pending, (state) => {
        state.isSavingPolicy = true;
      })
      .addCase(submitPolicy.fulfilled, (state) => {
        state.isSavingPolicy = false;
      })
      .addCase(submitPolicy.rejected, (state) => {
        state.isSavingPolicy = false;
        state.newPolicyCreated = false;
      });
  },
});

export function handleUpdatePolicy(newPolicyData) {
  return async (dispatch, getState) => {
    dispatch(updatePolicy(newPolicyData));
    const { policy } = await getState().accruals;
    sessionStorageHelper.setItem(sessionStorageKeys.ACCRUAL_POLICY, {
      ...policy,
      ...newPolicyData,
    });
  };
}

export function updateWizardStep(location) {
  return async (dispatch) => {
    const { current } = wizardStepTransformer(location);
    if (hasValue(current)) {
      dispatch(setWizardStep({ feature: current.name, step: current.step }));
    }
  };
}

export function handleWizardStepRedirect(location) {
  return async (dispatch) => {
    const { currentStep, feature } = wizardStepTransformer(location);
    dispatch(
      setWizardStep({
        feature,
        step: currentStep,
      })
    );
  };
}

// Helper Method for traverseing the wizard
// If ASC traverses towards HEAD
// if DESC traverses towards TAIL
async function handleWizardStep(
  { dispatch, getState },
  { history, location },
  ASC = false
) {
  const { policy, editing: isEditing } = await getState().accruals;
  const getURL = isEditing ? getAccrualsWizardEditLink : getAccrualsWizardLink;
  const { next, prev } = wizardStepTransformer(location);
  const link = ASC ? prev : next;

  if (hasValue(link)) {
    dispatch(setWizardStep({ feature: link.name, step: link.step }));
    history.push(getURL(policy?.ID, link.name));
  }
}

export function handleNextWizardStep(history, location) {
  return async (dispatch, getState) =>
    await handleWizardStep({ dispatch, getState }, { history, location });
}

export function handlePrevWizardStep(history, location) {
  return async (dispatch, getState) => {
    await handleWizardStep({ dispatch, getState }, { history, location }, true);
  };
}

export function handleCancelWizard(history) {
  return async (dispatch) => {
    dispatch(cancelWizard());
    history.push(`/${route.policies}`);
  };
}

export function clearCache() {
  sessionStorageHelper.removeItem(sessionStorageKeys.ACCRUAL_POLICY);
}

export function handleWillUnmount() {
  return async (dispatch, getState) => {
    const { storage, policy } = await getState().accruals;

    if (policy?.ID) {
      dispatch(cancelWizard());
      dispatch(clearPolicy());
      sessionStorageHelper.removeItem(sessionStorageKeys.ACCRUAL_POLICY);

      sessionStorageHelper.setItem(
        sessionStorageKeys.ACCRUAL_DO_NOT_SHOW_FOR_EDIT,
        storage.doNotShowDialogForEdit
      );

      sessionStorageHelper.setItem(
        sessionStorageKeys.ACCRUAL_DO_NOT_SHOW_FOR_GO_BACK,
        storage.doNotShowDialogForGoBackToPolicies
      );
    }
  };
}

export function addPolicy(history) {
  return async (dispatch) => {
    const id = uuidv4();
    const url = getAccrualsWizardLink(id, wizardStepModel.create);
    history.push(url);
    dispatch(setWizardRunning(true)); // signal to let anthing know we are now started the wizard
  };
}

export function doNotShowDialogForGoBackToPolicies(doNotShow) {
  return async (dispatch) => {
    dispatch(setDoNotShowDialogForGoBackToPolicies({ value: doNotShow }));
  };
}

export function doNotShowDialogForEdit(doNotShow) {
  return async (dispatch) => {
    dispatch(setDoNotShowDialogForEdit({ value: doNotShow }));
  };
}

export function removePolicySelectedEmployees(selected) {
  return async (dispatch, getState) => {
    const {
      policy: { SelectedEmployees },
    } = await getState().accruals;

    const keys = selected.map((e) => e.EmployeeId);
    const newSelected = SelectedEmployees?.filter((e) => {
      return keys.indexOf(e.EmployeeId) === -1;
    });

    dispatch(updatePolicy({ SelectedEmployees: newSelected }));
  };
}

export function resetNewPolicyCreateAnimation() {
  return async (dispatch, getState) => {
    const { newPolicyCreated } = await getState().accruals;
    if (newPolicyCreated) {
      await sleep(4000); // the transition is only 1sec.
      dispatch(setNewPolicyCreated(false));
    }
  };
}

export const {
  cancelWizard,
  clearPolicy,
  setDoNotShowDialogForGoBackToPolicies,
  setDoNotShowDialogForEdit,
  setEditing,
  setManagedInTwp,
  setNewPolicyCreated,
  setPolicies,
  setSaving,
  setWizardStep,
  setWizardRunning,
  updatePolicy,
} = slice.actions;

export const wizardSelector = (state) => state.accruals.wizard;
export const punchCategoriesSelector = (state) =>
  state.accruals.punchCategories;
export const policySelector = (state) => state.accruals.policy;
export const policiesSelector = (state) => state.accruals.policies;

export function useAccrualsDoNotShowDialogForGoBackToPolicies() {
  return useSelector(
    (state) => state.accruals.storage.doNotShowDialogForGoBackToPolicies
  );
}

export function useAccrualsDoNotShowDialogForEdit() {
  return useSelector((state) => state.accruals.storage.doNotShowDialogForEdit);
}

export function useAccrualsWizardHasSummary() {
  const wizard = useSelector(wizardSelector, deepEqual);
  return wizard.hasSummary && !wizard.summaryHidden;
}

export function useAccrualsWizardIsRunning() {
  const wizard = useSelector(wizardSelector, deepEqual);
  return wizard.isRunning;
}

export function useAccrualsIsEditing() {
  return useSelector((state) => state.accruals.editing);
}

export function useAccrualsIsFetchingPolicies() {
  return useSelector((state) => state.accruals.isFetchingPolicies);
}

export function useAccrualsIsRemovingPolicy() {
  return useSelector((state) => state.accruals.isRemovingPolicy);
}

export function useAccrualsIsSavingPolicy() {
  return useSelector((state) => state.accruals.isSavingPolicy);
}

export function useAccrualsWizardPolicy() {
  return useSelector(policySelector, deepEqual);
}

export function useAccrualsPunchCategories() {
  return useSelector(punchCategoriesSelector, deepEqual);
}

export function useAccrualsPolicies() {
  return useSelector(policiesSelector, deepEqual);
}

export function useNewPolicyCreated() {
  return useSelector((state) => state.accruals.newPolicyCreated);
}

export function useManagedInTwp() {
  return useSelector((state) => state.accruals.ManagedInTwp);
}

export default slice.reducer;
